import { toast as sonnerToast, type ExternalToast } from "vue-sonner";
import { toastManager } from "@/utils/toast-manager";

const MAX_TOAST_LENGTH = 120;

interface ToastOptions extends ExternalToast {
  duration?: number;
}

function truncateText(text: string, maxLength: number = MAX_TOAST_LENGTH): string {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + "...";
}

function showToast(
  message: string,
  type: "success" | "error" | "warning" | "info",
  options?: ToastOptions
) {
  // Validate message
  const validatedMessage = toastManager.validateMessage(message);
  if (!validatedMessage) return null;

  // Check for duplicates (3s window)
  if (toastManager.isDuplicate(validatedMessage)) return null;

  // Enforce queue limit (max 3)
  toastManager.enforceLimit();

  // Truncate message
  const truncatedMessage = truncateText(validatedMessage);

  // Calculate dynamic duration
  const defaultDuration = type === "error" ? 5000 : 4000;
  const duration = options?.duration || (truncatedMessage.length > 80 ? 5000 : defaultDuration);

  // Show toast with vue-sonner
  const toastId = sonnerToast[type](truncatedMessage, {
    duration,
    position: "top-right",
    closeButton: true,
    ...options,
  });

  // Track toast for queue management
  if (toastId) {
    toastManager.trackToast(validatedMessage, toastId);
  }

  return toastId;
}

export function useToast() {
  const error = (message: string, options?: ToastOptions) => showToast(message, "error", options);

  return {
    success: (message: string, options?: ToastOptions) => showToast(message, "success", options),
    error,
    danger: error, // Alias for backwards compatibility
    warning: (message: string, options?: ToastOptions) => showToast(message, "warning", options),
    info: (message: string, options?: ToastOptions) => showToast(message, "info", options),
    clear: () => sonnerToast.dismiss(),
    clearAll: () => toastManager.clearAll(),
  };
}
